import type { RouteObject } from 'react-router-dom';

import { AppLayout } from '@layouts/app-layout/AppLayout';
import { CustomerDocumentsRouterPage } from '@pages/customer-documents/CustomerDocumentsRouterPage';
import { CaeDocumentsPage } from '@pages/documents/cae/CaeDocumentsPage';
import { CompanyDocumentsPage } from '@pages/documents/company/CompanyDocumentsPage';
import { DocumentsIndexRedirect } from '@pages/documents/components/documents-index-redirect/DocumentsIndexRedirect';
import { DocumentsPage } from '@pages/documents/DocumentsPage';
import { CompanyDocumentsGuard } from '@pages/documents/routes/company-documents-guard/CompanyDocumentsGuard';
import { SubmitDocumentsRoute } from '@pages/documents/routes/submit-documents-route/SubmitDocumentsRoute';
import { SubmittedDocumentsPage } from '@pages/documents/submitted/SubmittedDocumentsPage';
import { EtcaRedirectPage } from '@pages/etca-redirect/EtcaRedirectPage';
import { EtcaRouterPage } from '@pages/etca/EtcaRouterPage';
import { HomePage } from '@pages/home/HomePage';
import { InstructorsPage } from '@pages/instructors/InstructorsPage';
import { InstructorApprovalLetterRoute } from '@pages/instructors/routes/approval-letter-route/InstructorApprovalLetterRoute';
import { InstructorDetailRoute } from '@pages/instructors/routes/instructor-detail-route/InstructorDetailRoute';
import { InstructorManageInstructorsRoute } from '@pages/instructors/routes/manage-instructors-route/InstructorManageInstructorsRoute';
import { InvoicesPage } from '@pages/invoices/InvoicesPage';
import { InvoiceDetailRoute } from '@pages/invoices/routes/invoice-detail-route/InvoiceDetailRoute';
import { RecordsRouterPage } from '@pages/records/RecordsRouterPage';
import { ReservationRequestsRouterPage } from '@pages/reservation-requests/ReservationRequestsRouterPage';
import { ReservationsRouterPage } from '@pages/reservations/ReservationsRouterPage';
import { SalesforceRedirectPage } from '@pages/salesforce-redirect/SalesforceRedirectPage';
import { UserChangeRequestRoute } from '@pages/users/routes/user-change-request-route/UserChangeRequestRoute';
import { UserCreateRoute } from '@pages/users/routes/user-create-route/UserCreateRoute';
import { UserDetailRoute } from '@pages/users/routes/user-detail-route/UserDetailRoute';
import { UsersPage } from '@pages/users/UsersPage';
import { AuthenticatedRoute } from '@shared/components/route-guards/authenticated-route/AuthenticatedRoute';
import { FeatureFlagGuard } from '@shared/components/route-guards/feature-flag-guard/FeatureFlagGuard';
import { OrganizationRouteGuard } from '@shared/components/route-guards/organization-route-guard/OrganizationRouteGuard';
import { Part135Guard } from '@shared/components/route-guards/part135-guard/Part135Guard';
import { PermissionGuard } from '@shared/components/route-guards/permission-guard/PermissionGuard';

import { APP_ROUTES, INSTRUCTORS_ROUTE_SEGMENTS } from './routes.constants';

/**
 * Manage module routes - for Managers (org-scoped)
 * Base path: /manage/:customerId
 *
 * Guard stack:
 * 1. AuthenticatedRoute - User must be logged in
 * 2. OrganizationRouteGuard - Validate org access and auto-select
 *
 * For feature-flagged routes within Manage, add:
 * 3. FeatureFlagGuard - Check feature flag
 * 4. PermissionGuard - Check user permission
 */
export const manageRoutes: RouteObject[] = [
  {
    path: APP_ROUTES.MANAGE_SF_REDIRECT,
    element: (
      <AuthenticatedRoute authType="b2e">
        <SalesforceRedirectPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: APP_ROUTES.MANAGE_ETCA_REDIRECT,
    element: (
      <AuthenticatedRoute>
        <EtcaRedirectPage />
      </AuthenticatedRoute>
    ),
  },
  {
    path: '/manage/:customerId',
    element: (
      <AuthenticatedRoute>
        <OrganizationRouteGuard>
          <AppLayout />
        </OrganizationRouteGuard>
      </AuthenticatedRoute>
    ),
    children: [
      {
        path: 'home',
        element: <HomePage />,
      },
      {
        path: 'reservation-requests/*',
        element: (
          <FeatureFlagGuard flag="bookingRequestPage">
            <PermissionGuard permission="ReservationRequests_View_Org">
              <ReservationRequestsRouterPage />
            </PermissionGuard>
          </FeatureFlagGuard>
        ),
      },
      {
        path: 'reservations/*',
        element: (
          <FeatureFlagGuard flag="reservationsPageV2">
            <PermissionGuard permission="Reservations_View_Org">
              <ReservationsRouterPage />
            </PermissionGuard>
          </FeatureFlagGuard>
        ),
      },
      {
        path: 'invoices-and-payments',
        element: (
          <FeatureFlagGuard flag="invoicesV2WebApp">
            <PermissionGuard permission="Invoices_View_Org">
              <InvoicesPage />
            </PermissionGuard>
          </FeatureFlagGuard>
        ),
        children: [
          {
            path: APP_ROUTES.MANAGE_INVOICES_DETAIL,
            element: <InvoiceDetailRoute />,
          },
        ],
      },
      {
        path: INSTRUCTORS_ROUTE_SEGMENTS.LIST,
        element: (
          <FeatureFlagGuard flag="instructorManagementV2WebApp">
            <Part135Guard>
              <PermissionGuard permission="InstructorApproval_View_Org">
                <InstructorsPage />
              </PermissionGuard>
            </Part135Guard>
          </FeatureFlagGuard>
        ),
        children: [
          {
            path: INSTRUCTORS_ROUTE_SEGMENTS.DETAIL,
            element: <InstructorDetailRoute />,
          },
          {
            path: INSTRUCTORS_ROUTE_SEGMENTS.MANAGE_INSTRUCTORS,
            element: <InstructorManageInstructorsRoute />,
          },
          {
            path: INSTRUCTORS_ROUTE_SEGMENTS.APPROVAL_LETTER,
            element: <InstructorApprovalLetterRoute />,
          },
        ],
      },
      {
        path: 'records/*',
        element: (
          <PermissionGuard permission="Records_View_Org">
            <RecordsRouterPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'users',
        element: (
          <PermissionGuard permission="Users_View_Org">
            <UsersPage />
          </PermissionGuard>
        ),
        children: [
          {
            path: 'new',
            element: (
              <PermissionGuard permission="Users_Add_Org">
                <UserCreateRoute />
              </PermissionGuard>
            ),
          },
          {
            path: ':userId/change-request',
            element: (
              <PermissionGuard permission="UsersChangeRequest_Add_Org">
                <UserChangeRequestRoute />
              </PermissionGuard>
            ),
          },
          {
            path: ':userId',
            element: <UserDetailRoute />,
          },
        ],
      },
      {
        path: 'documents',
        element: (
          <PermissionGuard
            permission={[
              'CustomerTrainingDocument_View_Org',
              'CAETrainingDocuments_View_All',
              'SubmittedTrainingDocuments_View_Org',
            ]}
            operator="some"
          >
            <DocumentsPage />
          </PermissionGuard>
        ),
        children: [
          { index: true, element: <DocumentsIndexRedirect /> },
          {
            path: 'company',
            element: (
              <PermissionGuard permission="CustomerTrainingDocument_View_Org">
                <CompanyDocumentsGuard>
                  <CompanyDocumentsPage />
                </CompanyDocumentsGuard>
              </PermissionGuard>
            ),
            children: [
              {
                path: 'submit-documents',
                element: (
                  <PermissionGuard permission="CustomerTrainingDocuments_Add_Org">
                    <SubmitDocumentsRoute />
                  </PermissionGuard>
                ),
              },
            ],
          },
          {
            path: 'customer-documents/*',
            element: (
              <FeatureFlagGuard flag="manageViewCustomerNonFaaDocuments">
                <PermissionGuard permission="CustomerTrainingDocument_View_Org">
                  <CustomerDocumentsRouterPage />
                </PermissionGuard>
              </FeatureFlagGuard>
            ),
          },
          {
            path: 'cae',
            element: (
              <PermissionGuard permission="CAETrainingDocuments_View_All">
                <CaeDocumentsPage />
              </PermissionGuard>
            ),
          },
          {
            path: 'submitted-documents',
            element: (
              <PermissionGuard permission="SubmittedTrainingDocuments_View_Org">
                <SubmittedDocumentsPage />
              </PermissionGuard>
            ),
            children: [
              {
                path: 'submit-documents',
                element: (
                  <PermissionGuard permission="CustomerTrainingDocuments_Add_Org">
                    <SubmitDocumentsRoute />
                  </PermissionGuard>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'etca/:trainingEventId',
        element: (
            <PermissionGuard permission="ReservationsTCA_FullAccess_Org">
              <EtcaRouterPage />
            </PermissionGuard>
        ),
      },
    ],
  },
];
